import React, { createContext, useContext, useState, useEffect } from 'react';
import { couponAPI } from '../services/api';

const CouponContext = createContext();

export const useCoupon = () => {
  const context = useContext(CouponContext);
  if (!context) {
    throw new Error('useCoupon must be used within a CouponProvider');
  }
  return context;
};

const normalizeCoupon = (c) => ({
  id: c.id ?? c.code,
  code: c.code,
  description: c.description ?? c.name ?? '',
  type: (c.discountType || c.type || 'PERCENTAGE').toLowerCase().includes('percent') ? 'percentage' : (c.discountType || c.type || '').toLowerCase().includes('fixed') ? 'fixed' : 'freeShipping',
  value: c.discountValue ?? c.value ?? 0,
  minPurchase: c.minimumOrderAmount ?? c.minPurchase ?? 0,
  maxDiscount: c.maxDiscountAmount ?? c.maxDiscount ?? null,
  expiryDate: c.expiryDate ?? c.endDate,
  isActive: c.active !== false
});

export const CouponProvider = ({ children }) => {
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [availableCoupons, setAvailableCoupons] = useState([]);

  useEffect(() => {
    const savedCoupon = localStorage.getItem('appliedCoupon');
    if (savedCoupon) {
      try {
        setAppliedCoupon(JSON.parse(savedCoupon));
      } catch (e) {
        console.error('Error loading coupon:', e);
      }
    }
  }, []);

  useEffect(() => {
    if (appliedCoupon) {
      localStorage.setItem('appliedCoupon', JSON.stringify(appliedCoupon));
    } else {
      localStorage.removeItem('appliedCoupon');
    }
  }, [appliedCoupon]);

  const loadAvailableCoupons = async () => {
    try {
      const res = await couponAPI.getActiveCoupons();
      const list = Array.isArray(res.data) ? res.data : (res.data?.data ?? res.data ?? []);
      setAvailableCoupons(Array.isArray(list) ? list.map(normalizeCoupon) : []);
    } catch (e) {
      console.error('Error loading coupons:', e);
      setAvailableCoupons([]);
    }
  };

  useEffect(() => {
    loadAvailableCoupons();
  }, []);

  const validateCoupon = async (code, cartTotal) => {
    try {
      const res = await couponAPI.validateCoupon(code, cartTotal);
      const data = res.data?.data ?? res.data;
      if (data && (data.valid === true || data.valid)) {
        const byCode = await couponAPI.getCouponByCode(code).catch(() => null);
        const couponRaw = byCode?.data?.data ?? byCode?.data ?? byCode;
        return { valid: true, coupon: normalizeCoupon(couponRaw || { code }) };
      }
      return { valid: false, error: data?.message || 'invalidCoupon' };
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || 'invalidCoupon';
      return { valid: false, error: msg };
    }
  };

  const applyCoupon = async (code, cartTotal) => {
    setCouponError('');
    const validation = await validateCoupon(code, cartTotal);
    if (!validation.valid) {
      setCouponError(validation.error);
      return false;
    }
    setAppliedCoupon(validation.coupon);
    return true;
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponError('');
  };

  const calculateDiscount = (cartTotal, shippingCost = 0) => {
    if (!appliedCoupon) {
      return { discount: 0, freeShipping: false, finalTotal: cartTotal + shippingCost };
    }
    let discount = 0;
    let freeShipping = false;
    if (appliedCoupon.type === 'percentage') {
      discount = (cartTotal * appliedCoupon.value) / 100;
      if (appliedCoupon.maxDiscount) discount = Math.min(discount, appliedCoupon.maxDiscount);
    } else if (appliedCoupon.type === 'fixed') {
      discount = appliedCoupon.value;
    } else if (appliedCoupon.type === 'freeShipping') {
      freeShipping = true;
    }
    const finalShipping = freeShipping ? 0 : shippingCost;
    const finalTotal = Math.max(0, cartTotal - discount + finalShipping);
    return { discount, freeShipping, finalTotal, shippingSaved: freeShipping ? shippingCost : 0 };
  };

  const getAvailableCoupons = () => availableCoupons;

  const value = {
    appliedCoupon,
    couponError,
    applyCoupon,
    removeCoupon,
    calculateDiscount,
    getAvailableCoupons,
    validateCoupon
  };

  return (
    <CouponContext.Provider value={value}>
      {children}
    </CouponContext.Provider>
  );
};
